// src/pages/feedback/components/FeedbackSearchBar.tsx
//
// 검색 · 상태 필터 · 정렬 바. RentalSearchBar.tsx 를 골격째 복제했다 —
// 흰 서피스 + #E4E7EA 보더 + r12, 필드 배경 #F9FAFB, 선택된 토글은 액센트
// 틴트(#FDEDE8) + #C93A14.
//
// 유형 필터는 여기 없다 — 목록 상단의 탭이 담당한다(같은 축의 필터를 두
// 컨트롤로 나누면 사용자가 어느 쪽이 이기는지 알 수 없다).

import { useEffect, useRef, useState } from "react";
import {
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import { designTokens } from "../../../theme.ts";
import type {
  FeedbackListParams,
  FeedbackSort,
  FeedbackStatus,
} from "../../../contracts/feedback/types";
import { STATUS_ORDER, statusLabel } from "./feedbackVisuals.ts";

const T = designTokens;

const C = {
  surface: "#FFFFFF",
  field: "#F9FAFB",
  border: "#E4E7EA",
  ink: T.ink,
  sub: "#6B7178",
  accent: T.cargo,
  accentHover: "#C93A14",
  accentTint: "#FDEDE8",
  neutral: "#F2F4F5",
};

interface FeedbackSearchBarProps {
  value: FeedbackListParams;
  onChange: (next: FeedbackListParams) => void;
  /** 정렬 셀렉트를 숨긴다(정렬이 의미 없는 화면용). */
  hideSort?: boolean;
}

const STATUS_OPTIONS: Array<{ value: FeedbackStatus | ""; label: string }> = [
  { value: "", label: "전체" },
  ...STATUS_ORDER.map((s) => ({ value: s, label: statusLabel(s) })),
];

// 총괄 확정 4종. 서버 화이트리스트에는 comment_count_desc 도 있으나
// 이번 범위 밖이라 노출하지 않는다.
const SORT_OPTIONS: Array<{ value: FeedbackSort; label: string }> = [
  { value: "created_at_desc", label: "최신순" },
  { value: "created_at_asc", label: "오래된순" },
  { value: "updated_at_desc", label: "최근 수정순" },
  { value: "updated_at_asc", label: "오래된 수정순" },
];

export default function FeedbackSearchBar({
  value,
  onChange,
  hideSort = false,
}: FeedbackSearchBarProps) {
  const [text, setText] = useState(value.q ?? "");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 외부(URL 진입 · 필터 초기화)에서 q 가 바뀌면 입력값을 따라간다.
  useEffect(() => {
    if ((value.q ?? "") !== text) setText(value.q ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.q]);

  // 언마운트 시 대기 중인 디바운스를 정리한다 — 사라진 화면의 콜백이
  // 뒤늦게 부모 state 를 밀어 넣는 걸 막는다.
  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setText(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // 검색어가 바뀌면 항상 첫 페이지로 — 3페이지에서 검색하면 결과가
    // 없는 페이지에 착지한다.
    debounceRef.current = setTimeout(() => {
      onChange({ ...value, q: next, page: 0 });
    }, 300);
  };

  const handleStatusChange = (
    _e: React.MouseEvent<HTMLElement>,
    nextStatus: FeedbackStatus | "" | null
  ) => {
    if (nextStatus === null) return; // 선택 해제는 무시 — 항상 하나가 선택된다
    onChange({ ...value, status: nextStatus, page: 0 });
  };

  return (
    <Box
      sx={{
        bgcolor: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: "12px",
        boxShadow: "0 1px 2px rgba(16,24,32,0.06)",
        p: { xs: 1.75, md: 2 },
        mb: 2.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          gap: { xs: 1.5, lg: 2 },
          alignItems: { lg: "center" },
          flexWrap: "wrap",
        }}
      >
        {/* 검색어 */}
        <TextField
          value={text}
          onChange={handleTextChange}
          placeholder="제목 · 본문 검색"
          size="small"
          fullWidth
          inputProps={{ "aria-label": "제목 · 본문 검색" }}
          sx={{
            minWidth: { xs: "100%", lg: 280 },
            flex: { lg: 1 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              bgcolor: C.field,
              fontFamily: T.fonts.sans,
              fontSize: 14,
            },
            "& .MuiInputBase-input": { py: "9px" },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 0.75 }}>
                <SearchIcon sx={{ fontSize: 18, color: C.sub }} />
              </InputAdornment>
            ),
          }}
        />

        {/* 상태 필터 */}
        <ToggleButtonGroup
          value={value.status ?? ""}
          exclusive
          onChange={handleStatusChange}
          size="small"
          aria-label="처리 상태 필터"
          sx={{
            flexWrap: "wrap",
            gap: 0.6,
            "& .MuiToggleButton-root": {
              border: `1px solid ${C.border}`,
              borderRadius: "8px",
              bgcolor: C.surface,
              fontFamily: T.fonts.sans,
              fontWeight: 500,
              fontSize: 13,
              px: 1.5,
              py: 0.5,
              color: C.ink,
              textTransform: "none",
              letterSpacing: 0,
              "&:hover": { bgcolor: C.neutral },
              "&.Mui-selected": {
                bgcolor: C.accentTint,
                borderColor: C.accentTint,
                color: C.accentHover,
                fontWeight: 600,
                "&:hover": { bgcolor: C.accentTint },
              },
            },
            "& .MuiToggleButtonGroup-grouped:not(:first-of-type)": {
              marginLeft: 0,
              borderLeft: `1px solid ${C.border}`,
              "&.Mui-selected": { borderLeftColor: C.accentTint },
            },
          }}
        >
          {STATUS_OPTIONS.map((opt) => (
            <ToggleButton key={opt.value || "all"} value={opt.value}>
              {opt.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* 정렬 */}
        {!hideSort && (
          <FormControl
            size="small"
            sx={{
              minWidth: 180,
              width: { xs: "100%", lg: "auto" },
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                bgcolor: C.field,
                fontFamily: T.fonts.sans,
                fontSize: 13.5,
              },
              "& .MuiInputLabel-root": {
                fontFamily: T.fonts.sans,
                fontSize: 13,
                fontWeight: 500,
                letterSpacing: 0,
                color: C.sub,
              },
              "& .MuiInputLabel-root.Mui-focused": { color: C.accent },
            }}
          >
            <InputLabel id="feedback-sort-label" shrink>
              정렬
            </InputLabel>
            <Select
              labelId="feedback-sort-label"
              label="정렬"
              notched
              value={value.sort ?? "created_at_desc"}
              onChange={(e) =>
                onChange({
                  ...value,
                  sort: e.target.value as FeedbackSort,
                  page: 0,
                })
              }
            >
              {SORT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
    </Box>
  );
}
